import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function UploadProgram() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // CSV headers
  const csvHeaders = [
    "id",
    "college_id",
    "program_id",
    "branch_id",
    "regulation_id",
    "year_number",
    "semester_number",
    "course_order",
    "course_code",
    "short_code",
    "course_level",
    "course_name",
    "course_type",
    "course_category",
    "elective_number",
    "credits",
    "total_minimum",
    "internal_maximum",
    "internal_minimum",
    "external_minimum",
    "external_maximum",
    "created_on",
    "created_by",
    "modified_on",
    "modified_by",
    "delete_status",
    "workflow_status",
    "course_part",
    "course_group",
    "status",
  ];

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "exam_course_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns (exact CSV headers + S.No + Errors)
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "id", headerName: "ID", width: 80 },
    { field: "college_id", headerName: "College ID", width: 100 },
    { field: "program_id", headerName: "Program Name", width: 180 },
    { field: "branch_id", headerName: "Branch Name", width: 180 },
    { field: "regulation_id", headerName: "Regulation", width: 120 },
    { field: "year_number", headerName: "Study Year", width: 110 },
    { field: "semester_number", headerName: "Semester", width: 110 },
    { field: "course_order", headerName: "Course Order", width: 120 },
    { field: "course_code", headerName: "Course Code", width: 150 },
    { field: "short_code", headerName: "Short Code", width: 150 },
    { field: "course_level", headerName: "Course Level", width: 150 },
    { field: "course_name", headerName: "Course Name", width: 220 },
    { field: "course_type", headerName: "Course Type", width: 150 },
    { field: "course_category", headerName: "Course Category", width: 150 },
    { field: "elective_number", headerName: "Elective Number", width: 150 },
    { field: "credits", headerName: "Credits", width: 100 },
    { field: "internal_minimum", headerName: "Int. Min", width: 110 },
    { field: "external_minimum", headerName: "Ext. Min", width: 110 },
    { field: "internal_maximum", headerName: "Int. Max", width: 110 },
    { field: "external_maximum", headerName: "Ext. Max", width: 110 },
    { field: "created_on", headerName: "Created On", width: 160 },
    { field: "created_by", headerName: "Created By", width: 180 },
    { field: "modified_on", headerName: "Modified On", width: 160 },
    { field: "modified_by", headerName: "Modified By", width: 180 },
    { field: "delete_status", headerName: "Delete Status", width: 130 },
    { field: "workflow_status", headerName: "Workflow Status", width: 150 },
    { field: "course_part", headerName: "Course Part", width: 130 },
    { field: "course_group", headerName: "Course Group", width: 130 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "errors",
      headerName: "Errors",
      width: 250,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // File selection
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  // Parse + validate
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];
          if (!row.course_code) errors.push("Missing course_code");
          if (!row.course_name) errors.push("Missing course_name");
          if (row.credits && isNaN(row.credits)) errors.push("Invalid credits");
          if (row.semester_number && isNaN(row.semester_number)) errors.push("Invalid semester");
          return {
            id: index + 1,
            sno: index + 1,
            ...row,
            errors,
          };
        });
        setPreviewData(validatedData);
      },
    });
  };
  // Confirm upload
  const handleConfirm = async () => {
    try {
      const validRows = previewData.filter((row) => !row.errors || row.errors.length === 0);
      if (validRows.length === 0) {
        alert("No valid rows to upload!");
        return;
      }
      const res = await axios.post(
        `${API_BASE_URL}/api/exam-courses-upload/${clientId}/${collegeId}/data`,
        { exam_courses: validRows }
      );
      alert(res.data.message || "Courses uploaded successfully");
      setPreviewData([]);
      setCsvFile(null);
    } catch (err) {
      console.error(err);
      alert("Error uploading data: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Download Template Button */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadTemplate}
        sx={{
          backgroundColor: "#388e3c",
          color: "#fff",

          "&:hover": { backgroundColor: "#2c6b2f" },
        }}
      >
        Download CSV
      </Button>

      {/* File upload */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            ml: 2,
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          Choose CSV
        </Button>
      </label>

      <Button
        sx={{
          ml: 2,
          backgroundColor: "#ff9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#c77700" },
        }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview
      </Button>

      {/* DataGrid preview */}
      {previewData.length > 0 && (
        <>
          <Box sx={{ mt: 3, height: 500 }}>
            <DataGrid
              rows={previewData}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              disableRowSelectionOnClick
            />
          </Box>
          <Button
            sx={{
              mt: 2,
              backgroundColor: "#4caf50",
              color: "#fff",
              "&:hover": { backgroundColor: "#357a38" },
            }}
            variant="contained"
            onClick={handleConfirm}
          >
            Confirm & Upload
          </Button>
        </>
      )}
    </Box>
  );
}
